import axios from "axios";
import { onLogout } from "../helpers/helpers";
import store from "../store";
import { api_base_url } from "./config";
import history from "helpers/history";
import { showApiAlert } from "../constants/api_errors";

const handleApiCall = async (method, url, body, blockLoading, authUserCall = false) => {
  if (!blockLoading) {
    global.setLoading(true);
  }
  const token = localStorage.getItem("water_bill_access_token") || "";
  if (!authUserCall) {
    const authUser = await getAuthUserIfNotExists();
    if (!authUser) {
      history.navigate("/not-found");
      return;
    }
  }
  try {
    const response = await axios({
      baseURL: api_base_url,
      url: url,
      timeout: 60000,
      method: method,
      data: body,
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
    });
    global.setLoading(false);
    return response.data.data;
  } catch (error) {
    global.setLoading(false);
    if (error?.response?.status == 401 && error?.response?.data?.message === "TokenExpiredError") {
      return await refresh(method, url, body);
    } else if (error?.response?.status == 401) {
      onLogout();
    } else if (error?.response?.status == 422) {
      global.showAlert(
        "Validation Errors",
        error?.response?.data?.data[0]?.message || "error",
        "error"
      );
      return null;
    } else if (error?.response?.status == 404) {
      history.navigate("/not-found");
      return null;
    } else if (error?.response?.status == 500 || error?.response?.status == 400) {
      console.log("error", error.response);
      showApiAlert(
        error.response.status,
        error.response.data?.message || error?.message || "error"
      );
      return null;
    } else {
      showApiAlert(500, error.response.data?.message || error?.message || "error");
      return null;
    }
  }
};

export const getAuthUserIfNotExists = async () => {
  const token = localStorage.getItem("water_bill_access_token") || "";
  if (token && !store.getState().authUser) {
    const user = await handleApiCall("GET", "/api/users/auth_user", null, true, true);
    if (user) {
      store.getActions().setAuthUser(user);
      return user;
    } else {
      return null;
    }
  }
  return true;
};

const refresh = async (method, url, body) => {
  const refreshToken = localStorage.getItem("water_bill_refresh_token");
  try {
    const response = await axios.post(`${api_base_url}/auth/refresh`, {
      refresh_token: refreshToken,
    });
    if (response.data) {
      localStorage.setItem("water_bill_access_token", response.data.data.access_token);
      localStorage.setItem("water_bill_refresh_token", response.data.data.refresh_token);
      store.getActions().setAuthUser(response.data.data.user);
      return await handleApiCall(method, url, body);
    }
  } catch (error) {}
};

export const loginCall = async (userName, password) => {
  const response = await handleApiCall("POST", "/auth/login", {
    user_name: userName,
    password: password,
  });

  if (response) {
    localStorage.setItem("water_bill_access_token", response.access_token);
    localStorage.setItem("water_bill_refresh_token", response.refresh_token);
    store.getActions().setAuthUser(response.user);
  }
  return response;
};

export const getUsers = async () => {
  const users = await handleApiCall("GET", "/api/users", null);
  return users;
};

export const createUserCall = async (data) => {
  const user = await handleApiCall("POST", "/api/users", data);
  return user;
};

export const updateUserCall = async (id, body) => {
  const user = await handleApiCall("PUT", `/api/users/${id}`, body);
  return user;
};

export const getAccountsCall = async (search, page = 1, limit = 10) => {
  const accounts = await handleApiCall(
    "GET",
    `/api/accounts?page=${page}&limit=${limit}${search ? `&search=${search}` : ""}`,
    null
  );
  return accounts;
};

export const getAccountCall = async (id) => {
  const account = await handleApiCall("GET", `/api/accounts/${id}`, null);
  return account;
};

export const createAccountCall = async (data) => {
  const account = await handleApiCall("POST", "/api/accounts", data);
  return account;
};

export const updateAccountCall = async (id, body) => {
  const account = await handleApiCall("PUT", `/api/accounts/${id}`, body);
  return account;
};

export const getUserPermissionsCall = async (id) => {
  const account = await handleApiCall("GET", `/api/permissions/users/${id}`, null);
  return account;
};

export const updateUserPermissionsCall = async (id, data) => {
  const account = await handleApiCall("PUT", `/api/permissions/users/${id}`, data);
  return account;
};

export const getAccountByNumberCall = async (accountNo) => {
  const account = await handleApiCall("GET", `/api/accounts/number/${accountNo}`, null);
  return account;
};

export const createPaymentCall = async (data) => {
  const payment = await handleApiCall("POST", "/api/payments", data);
  return payment;
};

export const changePasswordCall = async (data) => {
  const payment = await handleApiCall("POST", "/api/users/change_password", data);
  return payment;
};

export const getOrganizations = async () => {
  const users = await handleApiCall("GET", "/api/organizations", null);
  return users;
};

export const createOrganizationCall = async (data) => {
  const user = await handleApiCall("POST", "/api/organizations", data);
  return user;
};

export const updateOrganizationCall = async (id, body) => {
  const user = await handleApiCall("PUT", `/api/organizations/${id}`, body);
  return user;
};

export const createReadingCall = async (data) => {
  const reading = await handleApiCall("POST", "/api/readings", data);
  return reading;
};

export const getUnitCharges = async () => {
  const charges = await handleApiCall("GET", "/api/unit_charges", null);
  return charges;
};

export const updateUnitCharges = async (data) => {
  const charges = await handleApiCall("PUT", "/api/unit_charges", data);
  return charges;
};

export const getConsumptionReceiptData = async (accountId) => {
  const consumptionData = await handleApiCall("GET", `/api/print/consumption/${accountId}`);
  return consumptionData;
};

export const getPaymentReceiptData = async (accountId) => {
  const paymentData = await handleApiCall("GET", `/api/print/payment/${accountId}`);
  return paymentData;
};

export const getPaymentSummaryCall = async ({ page, limit = 10 }) => {
  const payments = await handleApiCall("GET", `/api/payments/summary?page=${page}&limit=${limit}`);
  return payments;
};

export const getPaymentHistoryCall = async ({ fromDate, toDate, settled, page, limit = 10 }) => {
  const payments = await handleApiCall(
    "GET",
    `/api/payments/history?from_date=${fromDate}&to_date=${toDate}&settled=${settled}&page=${page}&limit=${limit}`
  );
  return payments;
};

export const getPaymentSettlementCall = async ({ page, limit = 10 }) => {
  const settlements = await handleApiCall(
    "GET",
    `/api/payment-settlements?page=${page}&limit=${limit}`
  );
  return settlements;
};

export const createPaymentSettlementCall = async () => {
  const settlement = await handleApiCall("POST", `/api/payment-settlements`);
  return settlement;
};

export const approvePaymentSettlementCall = async (id) => {
  const response = await handleApiCall("POST", `/api/payment-settlements/${id}/approve`);
  return response;
};

export const uploadLogoCall = async (data) => {
  const response = await handleApiCall("POST", `/api/files/upload_logo`, data);
  return response;
};

export const uploadReadingCall = async (data) => {
  const response = await handleApiCall("POST", `/api/files/upload_reading`, data);
  return response;
};

export const bulkUpdateAccountsCall = async (data) => {
  const response = await handleApiCall("POST", `/api/accounts/bulk_update`, data);
  return response;
};

export const transferAccountCall = async (id, body) => {
  const account = await handleApiCall("POST", `/api/accounts/transfer/${id}`, body);
  return account;
};

export const addLateReadingCall = async (body) => {
  const reading = await handleApiCall("POST", `/api/late-readings`, body);
  return reading;
};

export const deleteLateReadingCall = async (id) => {
  const response = await handleApiCall("DELETE", `/api/late-readings/${id}`);
  return response;
};

export const getConsumptionSummaryCall = async ({ page = 1, limit = 10 }) => {
  const payments = await handleApiCall(
    "GET",
    `/api/accounts/consumption_summary?page=${page}&limit=${limit}`
  );
  return payments;
};

export const downloadPaymentSettlementPdf = async (id) => {
  const token = localStorage.getItem("water_bill_access_token") || "";
  try {
    const response = await axios({
      baseURL: api_base_url,
      url: `/api/payment-settlements/${id}/download`,
      method: "GET",
      responseType: "blob",
      headers: {
        Authorization: token,
      },
    });
    // Create a link to download the PDF
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `payment_settlement_${id}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  } catch (error) {
    window.alert('Failed to download PDF');
  }
};
